import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

const usePlotlyEvents = ({
  isLabelMode,
  layout, annotations,
  labelsRawRef, sectionsRef, tRawRef,
  setHoveredLabel, timerEtiquette,
  handlePhaseClickInternal,
  onLabelHover, onLabelUnhover, onPhaseClick,
  signalCount,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [signalHover, setSignalHover] = useState(null);
  const plotElRef = useRef(null);
  const signalHoverTimer = useRef(null);

  useEffect(() => () => {
    if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
  }, []);

  const activeLayout = useMemo(() => {
    if (hoveredIndex === null) return layout;
    const ann = annotations[hoveredIndex];
    return {
      ...layout,
      annotations: [...(layout.annotations || []), ...(ann ? [ann] : [])],
    };
  }, [layout, annotations, hoveredIndex]);

  const handleLabelHoverInternal = useCallback((t, x, y) => {
    clearTimeout(timerEtiquette.current);
    const tNum = Number(t);
    const all = labelsRawRef.current;
    if (!all.length) return;
    const found = all.reduce((best, l) =>
      Math.abs(Number(l.t) - tNum) < Math.abs(Number(best.t) - tNum) ? l : best
    );
    if (found) setHoveredLabel({ label: found, x, y });
  }, [setHoveredLabel]); // labelsRawRef and timerEtiquette are refs — stable, no need in deps

  const handleLabelUnhoverInternal = useCallback(() => {
    timerEtiquette.current = setTimeout(() => setHoveredLabel(null), 300);
  }, [setHoveredLabel]);

  const keepSignalOpen = useCallback(() => {
    if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
  }, []);

  const scheduleSignalDismiss = useCallback(() => {
    signalHoverTimer.current = setTimeout(() => setSignalHover(null), 300);
  }, []);

  const handleHover = useCallback((e) => {
    const pt = e.points?.[0];
    if (!pt) return;

    const nSignal = signalCount || 1;

    if (pt.curveNumber < nSignal) {
      if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
      const ts = tRawRef.current[pt.pointNumber];
      setSignalHover({
        ts, yVal: pt.y, pointNumber: pt.pointNumber,
        curveIndex: pt.curveNumber,
        clientX: e.event?.clientX, clientY: e.event?.clientY,
      });
      return;
    }

    setSignalHover(null);

    if (pt.customdata !== undefined) {
      if (pt.customdata?.type === 'phase') return;
      if (!plotElRef.current)
        plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
      if (plotElRef.current) plotElRef.current.style.cursor = 'pointer';
      if (isLabelMode) {
        handleLabelHoverInternal(pt.customdata, e.event?.clientX, e.event?.clientY);
      } else if (onLabelHover) {
        onLabelHover(pt.customdata, e.event?.clientX, e.event?.clientY);
      }
      return;
    }

    setHoveredIndex(pt.curveNumber - nSignal);
  }, [isLabelMode, handleLabelHoverInternal, onLabelHover, signalCount]);

  const handleUnhover = useCallback(() => {
    setHoveredIndex(null);
    signalHoverTimer.current = setTimeout(() => setSignalHover(null), 300);
    if (plotElRef.current) plotElRef.current.style.cursor = '';
    if (isLabelMode) {
      handleLabelUnhoverInternal();
    } else if (onLabelUnhover) {
      onLabelUnhover();
    }
  }, [isLabelMode, handleLabelUnhoverInternal, onLabelUnhover]);

  const handleClickAnnotation = useCallback((e) => {
    if (e.index === undefined) return;
    const sec = sectionsRef.current[e.index];
    if (!sec) return;
    const phaseData = {
      type: 'phase', tStart: sec.tStart, tEnd: sec.tEnd,
      label: sec.label, color: sec.color,
    };
    if (isLabelMode) {
      handlePhaseClickInternal(phaseData);
    } else if (onPhaseClick) {
      onPhaseClick(phaseData, e.event?.clientX, e.event?.clientY);
    }
  }, [isLabelMode, handlePhaseClickInternal, onPhaseClick]);

  const handleClick = useCallback((e) => {
    const pt = e.points?.[0];
    if (!pt || pt.customdata?.type !== 'phase') return;
    if (isLabelMode) {
      handlePhaseClickInternal(pt.customdata);
    } else if (onPhaseClick) {
      onPhaseClick(pt.customdata, e.event?.clientX, e.event?.clientY);
    }
  }, [isLabelMode, handlePhaseClickInternal, onPhaseClick]);

  return {
    activeLayout,
    signalHover, setSignalHover,
    keepSignalOpen, scheduleSignalDismiss,
    handleHover, handleUnhover, handleClick, handleClickAnnotation,
  };
};

export default usePlotlyEvents;
